import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, FormControlLabel, Switch,
  Divider, Button, TextField, Select, MenuItem
} from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import axios from 'axios';
import { computeApiBase } from './apiBase';

const API_BASE = computeApiBase();

const modelLabels = {
  'gpt-oss:20b': 'GPT-OSS 20B',
  'qwen2.5vl:32b-q4_K_M': 'Qwen2.5 VL 32B (Q4_K_M)',
  'qwen3:30b': 'Qwen3 30B',
  'llama4:scout': 'LLaMA 4 Scout',
  'llama4:scout-custom': 'LLaMA 4 Scout (custom)',
  'qwq:32b': 'QwQ 32B',
  'llama4:17b-scout-16e-instruct-q4_K_M': 'LLaMA 4 17B (Scout 16e Instruct Q4_K_M)',
  'qwen3:32b': 'Qwen3 32B',
  'aravhawk/llama4:109b': 'LLaMA 4 109B (Aravhawk)',
  'qwq:latest': 'QwQ Latest',
  'qwen2.5:72b': 'Qwen2.5 72B',
  'qwen2.5:3b': 'Qwen2.5 3B'
};

const fallbackModels = [
  'gpt-oss:20b',
  'qwen2.5vl:32b-q4_K_M',
  'qwen3:30b',
  'llama4:scout',
  'llama4:scout-custom',
  'qwq:32b',
  'llama4:17b-scout-16e-instruct-q4_K_M',
  'qwen3:32b',
  'aravhawk/llama4:109b',
  'qwq:latest',
  'qwen2.5:72b',
  'qwen2.5:3b'
];

const engineProfiles = [
  { id: 'qwen', label: 'Профиль Qwen', model: 'qwen3:30b' },
  { id: 'gpt', label: 'Профиль GPT', model: 'gpt-oss:20b' }
];

const SettingsPanel = ({
  open, onClose,
  autoscroll, setAutoscroll,
  darkMode, setDarkMode,
  handleClearHistory,
  handleClearHistoryServer,
  handleDownloadHistory,
  handleUploadHistory,
  selectedModel, setSelectedModel,
  activeEngineModel,
  engineSwitching,
  onSwitchEngine,
  modelsList, setModelsList,
  username, setUsername,
  authAccount,
  authBusy,
  authBootstrapRequired,
  authError,
  managedAccounts,
  onAuthLogin,
  onAuthBootstrap,
  onAuthLogout,
  onAuthCreateAccount,
  onRefreshServerChats,
  statusServer,
  onImageUpload,
  imageFile,
  debugPrompt,
  runnerMode, setRunnerMode,
  runnerInterval, setRunnerInterval,
  lastRunnerStatus,
  paused, setPaused,
  runnerMaxCycles, setRunnerMaxCycles,
  runnerCycleCount, setRunnerCycleCount,
  runnerSequenceStatus,
  onStartSequence,
  onStopSequence,
  filterDuplicates, setFilterDuplicates,
  runnerBusy,
  runnerSettingsHydrated,
  activeThreadId,
  taskStats,
  onRunExecutor,
  onQuickVerify,
  onRefreshTasks,
  onEnqueueTask,
  onManualTick
}) => {
  // Модели Ollama (загружаются динамически)
  const [availableModels, setAvailableModels] = useState(modelsList.length > 0 ? modelsList : fallbackModels);
  const [manualModel, setManualModel] = useState('');
  const [nick, setNick] = useState(username || localStorage.getItem('mutant-nick') || 'Гость');
  const [authLogin, setAuthLogin] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authDisplayName, setAuthDisplayName] = useState('');
  const [newAccountLogin, setNewAccountLogin] = useState('');
  const [newAccountPassword, setNewAccountPassword] = useState('');
  const [newAccountDisplayName, setNewAccountDisplayName] = useState('');

  // При открытии панели — тянем модели, если сервер живой
  useEffect(() => {
    if (!open) return;
    axios.get(`${API_BASE}/models`)
      .then(res => {
        if (Array.isArray(res.data?.models) && res.data.models.length > 0) {
          setAvailableModels(res.data.models);
          setModelsList(res.data.models);
          if (!res.data.models.includes(selectedModel)) {
            setSelectedModel(res.data.models[0] || '');
          }
        }
      })
      .catch(() => {
        setAvailableModels(fallbackModels);
        setModelsList(fallbackModels);
      });
    // eslint-disable-next-line
  }, [open]);

  // Темная тема — апдейт <body> глобально (круто если App тоже будет завязан)
  useEffect(() => {
    document.body.style.background = darkMode ? "#191C21" : "#f4f4f4";
    document.body.style.color = darkMode ? "#f5f5f5" : "#222";
  }, [darkMode]);

  // Username save в localStorage и в стейт
  useEffect(() => {
    setUsername(nick);
    localStorage.setItem('mutant-nick', nick);
    // eslint-disable-next-line
  }, [nick]);

  // POST settings при изменении модели/режима
  useEffect(() => {
    if (!runnerSettingsHydrated) return;
    axios.post(`${API_BASE}/runner/settings`, {
      mode: runnerMode,
      interval: runnerInterval,
      model: selectedModel
    }).catch(() => {});
  }, [runnerSettingsHydrated, selectedModel, runnerMode, runnerInterval]);

  // Ручное добавление модели
  const handleAddManualModel = () => {
    const name = manualModel.trim();
    if (!name) return;
    if (!availableModels.includes(name)) {
      setAvailableModels(prev => [...prev, name]);
      setModelsList(prev => [...prev, name]);
    }
    setSelectedModel(name);
    setManualModel('');
  };

  // Controls
  const [customCycleLimit, setCustomCycleLimit] = useState('');
  const [manualRunnerLoading, setManualRunnerLoading] = useState(false);
  const [taskInput, setTaskInput] = useState('');
  const [engineStatusText, setEngineStatusText] = useState('');
  const autoRunnerActive = runnerMode === 'auto' && !paused;
  const runnerStateText = autoRunnerActive ? 'AUTO' : (runnerMode === 'auto' ? 'PAUSED' : 'MANUAL');
  const runnerStateColor = autoRunnerActive ? '#42c5f5' : (runnerMode === 'auto' ? '#B9C0CA' : 'limegreen');
  const tickStateText = runnerBusy ? 'BUSY' : 'FREE';
  const tickStateColor = runnerBusy ? '#FFB84D' : 'limegreen';
  const sequenceRunning = !!runnerSequenceStatus?.running;
  const sequenceText = sequenceRunning
    ? `${Number(runnerSequenceStatus?.completed_steps || 0)}/${Number(runnerSequenceStatus?.max_steps || 0)}`
    : (runnerSequenceStatus?.status || 'idle');

  const handleCycleLimitSubmit = () => {
    const val = parseInt(customCycleLimit);
    if (!isNaN(val) && val > 0) {
      setRunnerMaxCycles(val);
      setRunnerCycleCount(0);
    }
    else if (val === 0) {
      setRunnerMaxCycles(null);
      setRunnerCycleCount(0);
    }
  };

  const handleManualRunnerTick = async () => {
    if (runnerBusy) return;
    if (typeof onManualTick === 'function') {
      onManualTick();
      return;
    }
    setManualRunnerLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/runner/tick`,
        {
          model: selectedModel,
          mode: 'thinker',
          query: 'system',
          username: username || '',
          thread_id: activeThreadId || ''
        }
      );
      if (res?.data?.ok === false && res?.data?.error === 'busy') {
        console.warn('Runner busy, skip manual tick');
      }
    } catch (e) {
      console.error("Ошибка запуска Runner вручную:", e);
    } finally {
      setManualRunnerLoading(false);
    }
  };

  const handleEngineSwitch = async () => {
    const target = String(selectedModel || '').trim();
    if (!target || !onSwitchEngine) return;
    setEngineStatusText('Переключаю движок...');
    const out = await onSwitchEngine(target);
    if (out?.ok) {
      setEngineStatusText(`Активно: ${out.model || target}`);
    } else {
      setEngineStatusText(`Ошибка переключения: ${out?.error || 'unknown'}`);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 370, p: 2, background: darkMode ? "#232730" : "#fff", minHeight: "100vh" }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          ⚙️ Настройки
        </Typography>

        <Typography sx={{ mb: 1 }}>
          Статус сервера:{" "}
          <span style={{ color: statusServer ? "limegreen" : "red", fontWeight: 600 }}>
            {statusServer ? "ONLINE" : "OFFLINE"}
          </span>
        </Typography>
        <Typography sx={{ mb: 1 }}>
          Раннер:{" "}
          <span style={{ color: runnerStateColor, fontWeight: 600 }}>
            {runnerStateText}
          </span>
        </Typography>
        <Typography sx={{ mb: 1 }}>
          Тик:{" "}
          <span style={{ color: tickStateColor, fontWeight: 600 }}>
            {tickStateText}
          </span>
        </Typography>

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={<Switch checked={autoscroll} onChange={() => setAutoscroll(!autoscroll)} />}
          label="Автоскролл"
          sx={{ mb: 1 }}
        />
        <FormControlLabel
          control={<Switch checked={darkMode} onChange={() => setDarkMode(!darkMode)} />}
          label="Тёмная тема"
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ mb: 1 }}>Имя пользователя (ник):</Typography>
        <TextField
          fullWidth
          value={nick}
          onChange={(e) => setNick(e.target.value)}
          size="small"
          placeholder="Введите имя"
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ mb: 1 }}>Аккаунт:</Typography>
        {authAccount ? (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 0.5 }}>
              Вошли как: <b>{authAccount.display_name || authAccount.login}</b>
              {authAccount.is_master ? ' · master' : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => onRefreshServerChats && onRefreshServerChats()}>
                Синхронизировать чаты
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={() => onAuthLogout && onAuthLogout()}>
                Выйти
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Логин"
              value={authLogin}
              onChange={(e) => setAuthLogin(e.target.value)}
              sx={{ mb: 1 }}
            />
            {authBootstrapRequired ? (
              <TextField
                fullWidth
                size="small"
                label="Отображаемое имя"
                value={authDisplayName}
                onChange={(e) => setAuthDisplayName(e.target.value)}
                sx={{ mb: 1 }}
              />
            ) : null}
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Пароль"
              value={authPassword}
              onChange={(e) => setAuthPassword(e.target.value)}
              sx={{ mb: 1 }}
            />
            {authError ? (
              <Typography sx={{ color: 'error.main', fontSize: 13, mb: 1 }}>{authError}</Typography>
            ) : null}
            <Button
              fullWidth
              disabled={authBusy}
              variant="contained"
              onClick={() => {
                if (authBootstrapRequired) {
                  onAuthBootstrap && onAuthBootstrap({
                    login: authLogin,
                    password: authPassword,
                    displayName: authDisplayName,
                  });
                } else {
                  onAuthLogin && onAuthLogin({
                    login: authLogin,
                    password: authPassword,
                  });
                }
              }}
            >
              {authBootstrapRequired ? 'Создать master-аккаунт' : 'Войти'}
            </Button>
          </Box>
        )}

        {authAccount?.is_master ? (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 1 }}>Новый аккаунт:</Typography>
            <TextField
              fullWidth
              size="small"
              label="Логин"
              value={newAccountLogin}
              onChange={(e) => setNewAccountLogin(e.target.value)}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Имя"
              value={newAccountDisplayName}
              onChange={(e) => setNewAccountDisplayName(e.target.value)}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Пароль"
              value={newAccountPassword}
              onChange={(e) => setNewAccountPassword(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Button
              fullWidth
              disabled={authBusy}
              variant="outlined"
              onClick={async () => {
                const out = await (onAuthCreateAccount && onAuthCreateAccount({
                  login: newAccountLogin,
                  password: newAccountPassword,
                  displayName: newAccountDisplayName,
                }));
                if (out?.ok) {
                  setNewAccountLogin('');
                  setNewAccountPassword('');
                  setNewAccountDisplayName('');
                }
              }}
            >
              Создать пользователя
            </Button>
            {Array.isArray(managedAccounts) && managedAccounts.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                {managedAccounts.map((item) => (
                  <Typography key={item.id} sx={{ fontSize: 13, opacity: 0.9 }}>
                    {item.display_name || item.login} ({item.login}){item.is_master ? ' · master' : ''}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Typography sx={{ mb: 1 }}>Модель LLM:</Typography>
        <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
          Активная в backend: <strong>{activeEngineModel || 'неизвестно'}</strong>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          {engineProfiles.map((p) => (
            <Button
              key={p.id}
              variant={(activeEngineModel || selectedModel) === p.model ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              disabled={engineSwitching}
              onClick={async () => {
                setSelectedModel(p.model);
                setEngineStatusText(`Переключаю на ${p.model}...`);
                const out = await (onSwitchEngine ? onSwitchEngine(p.model) : { ok: false, error: 'switch_unavailable' });
                if (out?.ok) {
                  setEngineStatusText(`Активно: ${out.model || p.model}`);
                } else {
                  setEngineStatusText(`Ошибка переключения: ${out?.error || 'unknown'}`);
                }
              }}
              title={`Один клик: ${p.model}`}
              sx={{ flex: 1 }}
            >
              {p.label}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            fullWidth
            size="small"
            sx={{ background: darkMode ? "#282c34" : "#fafafa" }}
          >
            {availableModels.map((model, idx) => (
              <MenuItem value={model} key={idx}>
                {modelLabels[model] || model}
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            color="primary"
            onClick={handleEngineSwitch}
            disabled={!selectedModel || engineSwitching}
            sx={{ minWidth: 100 }}
            title="Сделать модель активной на backend"
          >
            {engineSwitching ? '...' : 'Применить'}
          </Button>
        </Box>
        {engineStatusText ? (
          <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.8 }}>
            {engineStatusText}
          </Typography>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Ручной ввод"
            size="small"
            value={manualModel}
            onChange={(e) => setManualModel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddManualModel();
            }}
            placeholder="Имя модели (например, llama3:8b)"
            fullWidth
            sx={{ background: darkMode ? "#1e1f26" : "#fff" }}
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAddManualModel}
            sx={{ minWidth: 48, px: 1.5 }}
            title="Добавить модель"
          >
            <AddCircleOutlineIcon />
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>🧠 Runner</Typography>
        <FormControlLabel
          control={<Switch checked={runnerMode === 'auto'} onChange={() => {
            const nextAuto = runnerMode !== 'auto';
            setRunnerMode(nextAuto ? 'auto' : 'manual');
            if (nextAuto) setPaused(false);
          }} />}
          label={runnerMode === 'auto' ? "Автоматический режим" : "Ручной режим"}
          sx={{ mb: 2 }}
        />

        {runnerMode === 'auto' && (
          <>
            <Typography sx={{ mb: 2 }}>
              Авто‑режим: следующий тик запускается сразу после завершения текущего.
            </Typography>

            <Typography sx={{ mb: 1 }}>Ограничение циклов:</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label="Циклы"
                size="small"
                type="number"
                value={customCycleLimit}
                onChange={(e) => setCustomCycleLimit(e.target.value)}
                sx={{ background: darkMode ? "#282c34" : "#fff" }}
              />
              <Button variant="contained" onClick={handleCycleLimitSubmit}>OK</Button>
            </Box>

            <Typography sx={{ mb: 1 }}>Осталось запусков: {runnerMaxCycles === null ? '∞' : Math.max(runnerMaxCycles - runnerCycleCount, 0)}</Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={!filterDuplicates}
                  onChange={() => setFilterDuplicates(prev => !prev)}
                />
              }
              label={
                <>
                  {filterDuplicates ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />} Показ повт. ответов
                </>
              }
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                color={paused ? "success" : "warning"}
                startIcon={paused ? <PlayArrowIcon /> : <PauseIcon />}
                onClick={() => setPaused(!paused)}
              >
                {paused ? "Продолжить" : "Пауза"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={() => {
                  setRunnerMode('manual');
                  setPaused(false);
                  setRunnerCycleCount(0);
                }}
              >
                Стоп
              </Button>
            </Box>
          </>
        )}

        {runnerMode === 'manual' && (
          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={handleManualRunnerTick}
              disabled={manualRunnerLoading || runnerBusy}
            >
              {manualRunnerLoading ? 'Запуск...' : (runnerBusy ? '⏳ Занято' : '▶ Запустить Runner вручную')}
            </Button>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ mb: 1 }}>
            Sequence: <strong>{sequenceText}</strong>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={() => onStartSequence && onStartSequence()}
              disabled={sequenceRunning || runnerBusy}
            >
              Старт sequence
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              startIcon={<StopIcon />}
              onClick={() => onStopSequence && onStopSequence()}
              disabled={!sequenceRunning}
            >
              Стоп
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>🧰 Task Executor</Typography>
        <Typography sx={{ mb: 1 }}>
          Pending задач: {taskStats?.pending ?? '—'}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Проверить факт (ввод вручную)"
          value={taskInput}
          onChange={(e) => setTaskInput(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={async () => {
              const q = taskInput.trim();
              if (!q) return;
              setTaskInput('');
              if (onQuickVerify) {
                await onQuickVerify(q);
              } else {
                onEnqueueTask(q);
              }
            }}
          >
            Проверить
          </Button>
          <Button variant="outlined" onClick={onRefreshTasks}>
            Обновить
          </Button>
          <Button variant="outlined" color="primary" onClick={() => onRunExecutor(null, { includeAuto: false })}>
            Запустить executor
          </Button>
        </Box>

        {lastRunnerStatus && (
          <Box sx={{ background: darkMode ? '#212325' : '#f3f3f3', borderRadius: 2, p: 1, mb: 2 }}>
            <Typography variant="subtitle2">⏱ Последний результат:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {lastRunnerStatus.text || '[нет текста]'}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Button
          fullWidth
          variant="outlined"
          color="secondary"
          startIcon={<DeleteSweepIcon />}
          onClick={handleClearHistory}
          sx={{ mb: 1 }}
        >
          Очистить локально
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="warning"
          startIcon={<DeleteSweepIcon />}
          onClick={handleClearHistoryServer}
          sx={{ mb: 1 }}
        >
          Очистить сервер
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadHistory}
          sx={{ mb: 1 }}
        >
          Скачать историю
        </Button>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          startIcon={<UploadFileIcon />}
          component="label"
          sx={{ mb: 2 }}
        >
          Загрузить историю
          <input type="file" hidden onChange={handleUploadHistory} />
        </Button>

        <Divider sx={{ my: 2 }} />

        <Button
          fullWidth
          variant="outlined"
          color="success"
          startIcon={<ImageIcon />}
          component="label"
          sx={{ mb: 1 }}
        >
          Загрузить изображение
          <input type="file" accept="image/*" hidden onChange={(e) => {
            if (e.target.files.length > 0) {
              onImageUpload(e.target.files[0]);
            }
          }} />
        </Button>

        {imageFile && (
          <>
            <Typography variant="body2" sx={{ mb: 1, color: 'gray' }}>
              Изображение: {imageFile.name}
            </Typography>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img
                src={URL.createObjectURL(imageFile)}
                alt="предпросмотр"
                style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8 }}
              />
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          🧪 Отладочный промпт:
        </Typography>
        <Box sx={{
          maxHeight: 200,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          background: darkMode ? '#232730' : '#f5f5f5',
          fontFamily: 'monospace',
          p: 1,
          borderRadius: 2,
          fontSize: '0.8rem',
          color: darkMode ? '#ccc' : '#333',
          mb: 2
        }}>
          {debugPrompt || '[промпт не получен]'}
        </Box>

        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={onClose}
        >
          Закрыть настройки
        </Button>
      </Box>
    </Drawer>
  );
};

export default SettingsPanel;
